import React, { useEffect, useRef, useState } from 'react';
import { Loader, Mic, MicOff, Video, VideoOff, AlertCircle, Bot, Sparkles, Send } from 'lucide-react';
import StreamingAvatar, {
  AvatarQuality,
  StreamingEvents,
  TaskType,
} from '@heygen/streaming-avatar';
import { setupChromaKey } from '../utils/chromaKey';

interface LessonContext {
  title: string;
  description?: string;
  content?: string;
  orderIndex?: number;
}

interface HeyGenAvatarSDKProps {
  avatarId?: string;
  voiceId?: string;
  lessonContext?: LessonContext;
  onReady?: () => void;
  onError?: (error: Error) => void;
  className?: string;
  width?: string;
  height?: string;
  mobileWidth?: string;
  mobileHeight?: string;
}

const API_BASE = import.meta.env.VITE_API_BASE || 'https://techtree-lms.rr-startech-innovation.workers.dev/api';

export const HeyGenAvatarSDK: React.FC<HeyGenAvatarSDKProps> = ({
  avatarId = 'Anna_public_20240108',
  voiceId = '1bd001e7e50f421d891986aad5158bc8',
  lessonContext,
  onReady,
  onError,
  className = '',
  width = '400px',
  height = '400px',
  mobileWidth = '100%',
  mobileHeight = '300px'
}) => {
  const mediaRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const avatarRef = useRef<StreamingAvatar | null>(null);
  const stopChromaKeyRef = useRef<(() => void) | null>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [textInput, setTextInput] = useState('');
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [conversationHistory, setConversationHistory] = useState<Array<{ role: string; content: string }>>([]);

  useEffect(() => {
    const initializeAvatar = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Get session token from backend
        console.log('🔑 Fetching session token...');
        const tokenResponse = await fetch(`${API_BASE}/heygen/session-token`, {
          method: 'POST'
        });

        if (!tokenResponse.ok) {
          const errorText = await tokenResponse.text();
          throw new Error(`Failed to get session token: ${tokenResponse.status} - ${errorText}`);
        }

        const { token } = await tokenResponse.json();
        console.log('✅ Session token received');

        // Initialize HeyGen SDK
        console.log('🎬 Initializing StreamingAvatar SDK...');
        const avatar = new StreamingAvatar({ token });
        avatarRef.current = avatar;

        // Set up event listeners BEFORE calling createStartAvatar
        avatar.on(StreamingEvents.STREAM_READY, (event) => {
          console.log('✅ STREAM_READY event received:', event);
          if (event.detail && mediaRef.current) {
            console.log('📹 Setting video srcObject and playing...');
            mediaRef.current.srcObject = event.detail;
            mediaRef.current.onloadedmetadata = () => {
              mediaRef.current!.play().then(() => {
                console.log('✅ Video playing!');
                setStream(event.detail);
                setIsConnected(true);
                setIsLoading(false);

                // Start chroma key to remove green screen
                if (mediaRef.current && canvasRef.current) {
                  console.log('🎨 Starting chroma key...');
                  stopChromaKeyRef.current = setupChromaKey(
                    mediaRef.current,
                    canvasRef.current,
                    {
                      minHue: 60,
                      maxHue: 180,
                      minSaturation: 0.1,
                      threshold: 1.0,
                    }
                  );
                  console.log('✅ Chroma key active');
                }

                onReady?.();
              }).catch((playError) => {
                console.error('❌ Video play error:', playError);
                setError(`Video play failed: ${playError.message}`);
                setIsLoading(false);
              });
            };
          } else {
            console.error('❌ No stream detail in STREAM_READY event');
            setError('Stream not available in STREAM_READY event');
            setIsLoading(false);
          }
        });

        avatar.on(StreamingEvents.STREAM_DISCONNECTED, () => {
          console.log('⚠️ Stream disconnected');
          setIsConnected(false);
        });

        avatar.on(StreamingEvents.AVATAR_START_TALKING, (e) => {
          console.log('🗣️ Avatar started talking:', e);
        });

        avatar.on(StreamingEvents.AVATAR_STOP_TALKING, (e) => {
          console.log('🤫 Avatar stopped talking:', e);
        });

        // Start the avatar session
        console.log('🚀 Creating avatar session with:', {
          quality: 'High',
          avatarName: avatarId,
        });

        await avatar.createStartAvatar({
          quality: AvatarQuality.High,
          avatarName: avatarId,
        });

        console.log('✅ createStartAvatar completed, waiting for STREAM_READY event...');

      } catch (err) {
        console.error('❌ HeyGen Avatar Error:', err);
        console.error('Error details:', {
          message: err instanceof Error ? err.message : String(err),
          stack: err instanceof Error ? err.stack : undefined,
          type: typeof err,
          avatarId,
          voiceId
        });
        const errorMessage = err instanceof Error ? err.message : 'Failed to initialize avatar';
        setError(errorMessage);
        setIsLoading(false);
        onError?.(err as Error);
      }
    };

    initializeAvatar();

    return () => {
      if (stopChromaKeyRef.current) {
        stopChromaKeyRef.current();
      }
      if (avatarRef.current) {
        console.log('🛑 Cleaning up avatar session...');
        avatarRef.current.stopAvatar();
      }
    };
  }, [avatarId, voiceId, onReady, onError]);

  const handleSendMessage = async () => {
    if (!textInput.trim() || !avatarRef.current || !isConnected || isSpeaking) {
      return;
    }

    const userMessage = textInput.trim();
    setTextInput(''); // Clear input immediately

    try {
      // Unmute on first interaction
      if (isMuted && mediaRef.current) {
        mediaRef.current.muted = false;
        setIsMuted(false);
        console.log('🔊 Audio unmuted');
      }

      setIsSpeaking(true);
      console.log('💬 User message:', userMessage);

      // Get AI response from Alice
      const aiResponse = await fetch(`${API_BASE}/alice/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage,
          lessonContext: lessonContext || {
            title: 'General Learning Session',
            description: 'Interactive learning with Alice'
          },
          conversationHistory
        })
      });

      if (!aiResponse.ok) {
        throw new Error(`AI response failed: ${aiResponse.status}`);
      }

      const { response: aliceReply } = await aiResponse.json();
      console.log('🤖 Alice response:', aliceReply);

      // Update conversation history
      setConversationHistory(prev => [
        ...prev,
        { role: 'user', content: userMessage },
        { role: 'assistant', content: aliceReply }
      ]);

      // Make avatar speak the AI response (REPEAT mode - just speak our AI text)
      await avatarRef.current.speak({
        text: aliceReply,
        taskType: TaskType.REPEAT,
      });

      console.log('✅ Alice finished speaking');

    } catch (err) {
      console.error('❌ Chat error:', err);
      setError(err instanceof Error ? err.message : 'Failed to chat with Alice');

      // Fallback response
      try {
        await avatarRef.current?.speak({
          text: "I'm having trouble processing that right now. Could you try rephrasing your question?",
          taskType: TaskType.REPEAT,
        });
      } catch (speakErr) {
        console.error('Failed to speak error message:', speakErr);
      }
    } finally {
      setIsSpeaking(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Demo mode placeholder
  if (!isConnected && !isLoading && !error) {
    return (
      <div
        className={`relative rounded-2xl overflow-hidden bg-gradient-to-br from-purple-900 via-pink-800 to-red-900 shadow-2xl ${className}`}
        style={{
          width: `clamp(${mobileWidth}, 100%, ${width})`,
          height: `clamp(${mobileHeight}, 100%, ${height})`,
          maxWidth: width,
          aspectRatio: '1/1'
        }}
      >
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-8">
          <div className="w-32 h-32 mb-6 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center animate-pulse">
            <Bot className="w-16 h-16 text-white" />
            <Sparkles className="absolute top-2 right-2 w-8 h-8 text-pink-400" />
          </div>
          <h3 className="text-2xl font-bold mb-2">Alice AI Avatar</h3>
          <p className="text-center opacity-90">
            Your Interactive AI Coach
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col ${className}`}>
      <div
        className="relative rounded-2xl overflow-hidden shadow-2xl"
        style={{
          width: `clamp(${mobileWidth}, 100%, ${width})`,
          height: `clamp(${mobileHeight}, 100%, ${height})`,
          maxWidth: width,
          backgroundColor: 'transparent'
        }}
      >
      {/* Loading state */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-purple-900 via-pink-800 to-red-900 z-10 rounded-2xl">
          <div className="text-center">
            <Loader className="w-12 h-12 text-white animate-spin mx-auto mb-4" />
            <p className="text-white font-semibold">Initializing Alice Avatar...</p>
          </div>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-red-900/90 z-10 rounded-2xl">
          <div className="text-center p-6 max-w-md">
            <AlertCircle className="w-16 h-16 text-red-300 mx-auto mb-4 animate-pulse" />
            <p className="text-white font-bold text-lg mb-3">HeyGen Avatar Error</p>
            <p className="text-red-100 text-sm mb-4 font-mono bg-black/30 p-3 rounded">{error}</p>
            <p className="text-red-200 text-xs">Check browser console (F12) for more details</p>
          </div>
        </div>
      )}

      {/* Video element (hidden, source for chroma key) */}
      <video
        ref={mediaRef}
        className="hidden"
        autoPlay
        playsInline
        muted
      />

      {/* Canvas element (displays chroma-keyed video) */}
      <canvas
        ref={canvasRef}
        className="w-full h-full object-cover rounded-2xl"
      />

      {/* Connection status and audio toggle */}
      {isConnected && (
        <div className="absolute top-4 right-4 z-20 flex items-center space-x-2">
          {isMuted && (
            <div className="px-3 py-1 bg-yellow-500/90 backdrop-blur-sm rounded-full">
              <span className="text-white text-xs font-semibold">🔇 Muted - Send a message to hear Alice</span>
            </div>
          )}
          <div className="flex items-center space-x-2 px-3 py-1 bg-green-500/90 backdrop-blur-sm rounded-full">
            <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
            <span className="text-white text-sm font-semibold">Live</span>
          </div>
        </div>
      )}
      </div>

      {/* Chat Input - Styled to match course design */}
      {isConnected && (
        <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <input
            type="text"
            value={textInput}
            onChange={(e) => setTextInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type a message for Alice..."
            disabled={isSpeaking}
            className="flex-1 px-5 py-4 text-base rounded-2xl border-2 border-orange-200 bg-white focus:border-orange-400 focus:ring-2 focus:ring-orange-200 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-all placeholder:text-gray-400"
          />
          <button
            onClick={handleSendMessage}
            disabled={!textInput.trim() || isSpeaking}
            className="px-8 py-4 bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-2xl font-semibold text-base hover:from-red-600 hover:to-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-orange-500/50 hover:shadow-xl hover:shadow-orange-600/50 flex items-center justify-center space-x-2 min-w-[120px]"
          >
            {isSpeaking ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                <span className="hidden sm:inline">Speaking...</span>
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                <span className="hidden sm:inline">Send</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default HeyGenAvatarSDK;
